import { Duration, LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import * as React from "react";
import ReactSlider from "react-slider";
import { globalize } from "../../translate";

interface FixedTimeRangeSliderProps {
  minTime: LocalTime;
  maxTime: LocalTime;
  duration: Duration;

  /**
   * Granularity that the user is allowed to selected
   */
  minuteSteps: number;

  /**
   * The value of the startTime (left handle)
   */
  value: LocalTime;
  disabled?: boolean;

  /**
   * Emits the value of the startTime (left handle)
   */
  onChange: (value: LocalTime) => void;
}

function disableScroll() {
  document.ontouchmove = (e) => {
    e.preventDefault();
  };
}

function enableScroll() {
  document.ontouchmove = () => {
    return true;
  };
}

export class FixedTimeRangeSlider extends React.Component<FixedTimeRangeSliderProps> {
  shouldComponentUpdate(nextProps: FixedTimeRangeSliderProps) {
    return (
      !this.props.duration.equals(nextProps.duration) ||
      !this.props.maxTime.equals(nextProps.maxTime) ||
      !this.props.minTime.equals(nextProps.minTime) ||
      this.props.minuteSteps !== nextProps.minuteSteps ||
      !this.props.value.equals(nextProps.value) ||
      this.props.onChange !== nextProps.onChange ||
      this.props.disabled !== nextProps.disabled
    );
  }

  handleSliderMove(startTime: number, endTime: number) {
    let adjustedStartTime: number = startTime;
    if (
      endTime > timeToMinutesNumber(this.props.minTime, this.props.value.plus(this.props.duration))
    ) {
      // Right slider was moved
      adjustedStartTime = timeToMinutesNumber(
        this.props.minTime,
        minutesNumberToTime(this.props.minTime, endTime).minus(this.props.duration)
      );
    }
    this.props.onChange(minutesNumberToTime(this.props.minTime, adjustedStartTime));
  }

  render() {
    const isDisabled = this.props.disabled !== undefined && this.props.disabled;
    const is24Hours = this.props.minTime.equals(this.props.maxTime);

    return (
      <ReactSlider
        // https://github.com/mpowaga/react-slider/issues/137
        onBeforeChange={() => disableScroll()}
        onAfterChange={() => enableScroll()}
        className="FixedTimeRangeSlider__slider"
        max={is24Hours ? 1440 : timeToMinutesNumber(this.props.minTime, this.props.maxTime)}
        min={0}
        minDistance={this.props.duration.toMinutes()}
        pearling={true}
        value={[
          timeToMinutesNumber(this.props.minTime, this.props.value),
          timeToMinutesNumber(this.props.minTime, this.props.value.plus(this.props.duration)),
        ]}
        step={this.props.minuteSteps}
        onChange={(values) => {
          if (!Array.isArray(values) || values.length < 2) {
            throw new Error("The Impossible Happened");
          }
          this.handleSliderMove(values[0], values[1]);
        }}
        snapDragDisabled={true}
        withBars={true}
        disabled={isDisabled}
        barClassName={"FixedTimeRangeSlider__bar"}
      >
        <div
          className={
            "FixedTimeRangeSlider__handleStart " +
            (isDisabled ? "FixedTimeRangeSlider__disabledHandle" : "")
          }
        >
          <div className={"FixedTimeRangeSlider__handleTitle"}>Start time</div>
          {globalize().formatLocalTime(this.props.value, { time: "short" })}
        </div>
        <div
          className={
            "FixedTimeRangeSlider__handleEnd " +
            (isDisabled ? "FixedTimeRangeSlider__disabledHandle" : "")
          }
        >
          <div className={"FixedTimeRangeSlider__handleTitle"}>End time</div>
          {globalize().formatLocalTime(this.props.value.plus(this.props.duration), {
            time: "short",
          })}
        </div>
      </ReactSlider>
    );
  }
}

function timeToMinutesNumber(minTime: LocalTime, time: LocalTime): number {
  const arbitraryDate = LocalDate.of(2000, 1, 1);

  const startTimeReal = LocalDateTime.of(arbitraryDate, minTime);

  const timeReal = LocalDateTime.of(
    time.isBefore(minTime) ? arbitraryDate.plusDays(1) : arbitraryDate,
    time
  );

  return Duration.between(startTimeReal, timeReal).toMinutes();
}

function minutesNumberToTime(minTime: LocalTime, minutesNumber: number): LocalTime {
  return minTime.plus(Duration.ofMinutes(minutesNumber));
}
